import axios from "axios";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;


/**
 * Obtiene todos los proveedores de un cliente
 * @param {string} clientId - ID del cliente
 * @returns {Promise<Object>} - Respuesta con los proveedores
 */
export const getSuppliers = async (clientId) => {
  try {
    // Enviar evento para mostrar esqueleto
    const event = new CustomEvent('suppliersLoading', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.get(`${API_URL}/workflows/p2p/${clientId}/suppliers`);
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('suppliersLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('suppliersLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener los proveedores:", error.response?.data || error);
    throw error;
  }
};

/**
 * Obtiene un proveedor específico por su ID
 * @param {string} clientId - ID del cliente
 * @param {string} supplierId - ID del proveedor
 * @returns {Promise<Object>} - Respuesta con los datos del proveedor
 */
export const getSupplierById = async (clientId, supplierId) => {
  try {
    // Enviar evento para mostrar esqueleto
    const event = new CustomEvent('supplierLoading', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.get(`${API_URL}/workflows/p2p/${clientId}/suppliers/${supplierId}`);
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('supplierLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('supplierLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener el proveedor:", error.response?.data || error);
    throw error;
  }
};

/**
 * Función auxiliar para formatear los datos del proveedor
 * @param {Object} data - Datos del proveedor
 * @returns {Object} - Datos formateados
 */
const formatSupplierData = (data) => {
  const formattedData = { ...data };
  
  // Convertir capabilities a formato de array PostgreSQL
  if (formattedData.capabilities) {
    // Si es un string JSON, lo parseamos primero
    if (typeof formattedData.capabilities === 'string') {
      try {
        // Intentar parsear como JSON
        const parsed = JSON.parse(formattedData.capabilities);
        if (Array.isArray(parsed)) {
          // Convertir a formato de array PostgreSQL: {elemento1,elemento2,elemento3}
          formattedData.capabilities = `{${parsed.map(item => `"${item.replace(/"/g, '\\"')}"`).join(',')}}`;
        } else {
          // Si no es un array, crear un array con un solo elemento
          formattedData.capabilities = `{"${String(formattedData.capabilities).replace(/"/g, '\\"')}"}`;
        }
      } catch (e) {
        // Si no se puede parsear como JSON, asumir que es una cadena simple
        formattedData.capabilities = `{"${formattedData.capabilities.replace(/"/g, '\\"')}"}`;
      }
    } else if (Array.isArray(formattedData.capabilities)) {
      // Si ya es un array, convertirlo al formato PostgreSQL
      formattedData.capabilities = `{${formattedData.capabilities.map(item => `"${String(item).replace(/"/g, '\\"')}"`).join(',')}}`;
    } else {
      // Otro caso, convertir a string y crear un array de un elemento
      formattedData.capabilities = `{"${String(formattedData.capabilities).replace(/"/g, '\\"')}"}`;
    }
  } else {
    // Si no hay capabilities, enviamos un array vacío en formato PostgreSQL
    formattedData.capabilities = '{}';
  }
  
  return formattedData;
};

/**
 * Crea un nuevo proveedor
 * @param {string} clientId - ID del cliente
 * @param {Object} supplierData - Datos del proveedor a crear
 * @returns {Promise<Object>} - Respuesta con el proveedor creado
 */
export const createSupplier = async (clientId, supplierData) => {
  try {
    // Enviar evento para mostrar esqueleto o indicador de carga
    const event = new CustomEvent('supplierSaving', { detail: true });
    window.dispatchEvent(event);
    
    // Procesar datos para asegurar formato correcto
    const formattedData = formatSupplierData(supplierData);
    
    console.log("Datos formateados para enviar:", formattedData);

    // Enviar datos formateados al backend
    const response = await axios.post(`${API_URL}/workflows/p2p/${clientId}/suppliers`, formattedData);
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('supplierSaving', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('supplierSaving', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al crear el proveedor:", error.response?.data || error);
    throw error;
  }
};

/**
 * Actualiza un proveedor existente
 * @param {string} clientId - ID del cliente
 * @param {string} supplierId - ID del proveedor
 * @param {Object} supplierData - Datos actualizados del proveedor
 * @returns {Promise<Object>} - Respuesta con el proveedor actualizado
 */
export const updateSupplier = async (clientId, supplierId, supplierData) => {
  try {
    // Enviar evento para mostrar esqueleto o indicador de carga
    const event = new CustomEvent('supplierUpdating', { detail: true });
    window.dispatchEvent(event);
    
    // Procesar datos para asegurar formato correcto
    const formattedData = formatSupplierData(supplierData);
    
    console.log("Datos formateados para actualizar:", formattedData);

    // Enviar datos formateados al backend
    const response = await axios.patch(`${API_URL}/workflows/p2p/${clientId}/suppliers/${supplierId}`, formattedData);
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('supplierUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('supplierUpdating', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al actualizar el proveedor:", error.response?.data || error);
    throw error;
  }
};

/**
 * Elimina un proveedor
 * @param {string} clientId - ID del cliente
 * @param {string} supplierId - ID del proveedor a eliminar
 * @returns {Promise<Object>} - Respuesta de confirmación
 */
export const deleteSupplier = async (clientId, supplierId) => {
  try {
    // Enviar evento para mostrar esqueleto o indicador de carga
    const event = new CustomEvent('supplierDeleting', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.delete(`${API_URL}/workflows/p2p/${clientId}/suppliers/${supplierId}`);
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('supplierDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('supplierDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al eliminar el proveedor:", error.response?.data || error);
    throw error;
  }
};

/**
 * Obtiene proveedores con columnas específicas
 * @param {string} clientId - ID del cliente
 * @returns {Promise<Object>} - Respuesta con los proveedores y columnas específicas
 */
export const getSuppliersBasicInfo = async (clientId) => {
  try {
    // Enviar evento para mostrar esqueleto
    const event = new CustomEvent('suppliersBasicLoading', { detail: true });
    window.dispatchEvent(event);

    // Obtener todos los proveedores
    const response = await axios.get(`${API_URL}/workflows/p2p/${clientId}/suppliers`);
    
    // Filtrar solo las columnas requeridas
    const filteredData = response.data.success ? {
      ...response.data,
      data: response.data.data.map(supplier => ({
        id: supplier.id, // Siempre incluir el ID para referencia
        name: supplier.name,
        country: supplier.country,
        contact_email: supplier.contact_email,
        phone: supplier.phone,
        currency: supplier.currency,
        rating_service: supplier.rating_service,
        rating_punctuality: supplier.rating_punctuality,
        rating_quality: supplier.rating_quality,
        rating_overall: supplier.rating_overall,
        is_favorite: supplier.is_favorite,
        description: supplier.description,
        capabilities: supplier.capabilities,
        typicalleadtime: supplier.typicalleadtime,
        nit: supplier.nit,
      }))
    } : response.data;
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('suppliersBasicLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return filteredData;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('suppliersBasicLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener información básica de proveedores:", error.response?.data || error);
    throw error;
  }
};
/**
 * Obtiene los proveedores asociados a una orden específica usando SSE (Server-Sent Events)
 * @param {string} clientId - ID del cliente
 * @param {string} orderId - ID de la orden
 * @param {function} onUpdate - Callback que se ejecuta cuando hay nuevos datos
 * 
 * @returns {object} - Objeto con método para cerrar la conexión
 */
  export const getSuppliersByOrderId = (clientId, orderId, onUpdate, interval = 10000) => {
  // Enviar evento para mostrar esqueleto
  const event = new CustomEvent('orderSuppliersLoading', { detail: true });
  window.dispatchEvent(event);
  
  // Validar parámetros
  if (!clientId || !orderId) {
    console.error("Se requieren clientId y orderId para obtener proveedores");
    
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('orderSuppliersLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    throw new Error("Se requieren clientId y orderId");
  }
  
  // Crear URL con parámetros
    const url = `${API_URL}/workflows/p2p/${clientId}/iprs/${orderId}/suppliers?interval=${interval}`;
  
  // Crear conexión SSE
  const eventSource = new EventSource(url);
  
  // Manejar eventos de datos
  eventSource.onmessage = (event) => {
    try {
      const parsedData = JSON.parse(event.data);
      console.log("SSE: Recibidos datos de proveedores:", parsedData);
      
      // Formatear la respuesta para mantener compatibilidad con el código existente
      const formattedResponse = {
        success: true,
        data: parsedData.data,
        count: parsedData.count,
        timestamp: parsedData.timestamp
      };
      
      // Ejecutar callback con los datos
      if (typeof onUpdate === 'function') {
        onUpdate(formattedResponse);
      }
      
      // Ocultar esqueleto después de recibir datos iniciales
      const endEvent = new CustomEvent('orderSuppliersLoading', { detail: false });
      window.dispatchEvent(endEvent);
    } catch (error) {
      console.error("Error al procesar datos SSE:", error);
    }
  };
  
  // Manejar errores
  eventSource.onerror = (error) => {
    console.error("Error en la conexión SSE:", error);
    
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('orderSuppliersLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    // Cerrar conexión en caso de error
    eventSource.close();
    
    // Ejecutar callback con error
    if (typeof onUpdate === 'function') {
      onUpdate({
        success: false,
        message: "Error en la conexión con el servidor",
        error: error
      });
    }
  };
  
  // Manejar eventos de error específicos del servidor
  eventSource.addEventListener('error', (event) => {
    try {
      const errorData = JSON.parse(event.data);
      console.error("Error SSE del servidor:", errorData);
      
      // Ejecutar callback con error
      if (typeof onUpdate === 'function') {
        onUpdate({
          success: false,
          message: errorData.message,
          error: errorData.error
        });
      }
    } catch (e) {
      console.error("Error al procesar error SSE:", e);
    }
  });
  
  // Devolver objeto con método para cerrar la conexión
  return {
    close: () => {
      console.log("Cerrando conexión SSE de proveedores");
      eventSource.close();
    }
  };
};

/**
 * Crea un nuevo documento para un proveedor
 * @param {string} clientId - ID del cliente
 * @param {Object} documentData - Datos del documento a crear
 * @param {string} documentData.document_type - Tipo de documento
 * @param {string} documentData.url - URL del documento
 * @param {string} documentData.order_id - ID de la orden
 * @param {string} documentData.supplier_id - ID del proveedor 
 * @param {string} documentData.name - Nombre del documento
 * @returns {Promise<Object>} - Respuesta con el documento creado
 */
export const createDocument = async (clientId, documentData) => {
  try {
    // Enviar evento para mostrar esqueleto o indicador de carga
    const event = new CustomEvent('documentCreating', { detail: true });
    window.dispatchEvent(event);

    // Verificar datos requeridos
    if (!documentData.document_type || !documentData.url || !documentData.order_id || !documentData.name) {
      throw new Error("Se requieren: document_type, url, order_id y name");
    }

    const response = await axios.post(`${API_URL}/workflows/p2p/${clientId}/documents`, documentData);
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('documentCreating', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('documentCreating', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al crear el documento:", error.response?.data || error);
    throw error;
  }
};

/**
 * Obtiene documentos asociados a una orden y proveedor específicos
 * @param {string} clientId - ID del cliente
 * @param {string} orderId - ID de la orden
 * @param {string} supplierId - ID del proveedor
 * @returns {Promise<Object>} - Respuesta con los documentos
 */
export const getDocuments = async (clientId, orderId, supplierId) => {
  try {
    // Enviar evento para mostrar esqueleto
    const event = new CustomEvent('documentsLoading', { detail: true });
    window.dispatchEvent(event);

    // Verificar datos requeridos
    if (!orderId || !supplierId) {
      throw new Error("Se requieren order_id y supplier_id");
    }

    const response = await axios.get(
      `${API_URL}/workflows/p2p/${clientId}/documents?order_id=${orderId}&supplier_id=${supplierId}`
    );
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('documentsLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('documentsLoading', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al obtener documentos:", error.response?.data || error);
    throw error;
  }
};

/**
 * Elimina un documento específico
 * @param {string} clientId - ID del cliente
 * @param {string} documentId - ID del documento a eliminar
 * @returns {Promise<Object>} - Respuesta de confirmación
 */
export const deleteDocument = async (clientId, documentId) => {
  try {
    // Enviar evento para mostrar esqueleto o indicador de carga
    const event = new CustomEvent('documentDeleting', { detail: true });
    window.dispatchEvent(event);

    // Verificar datos requeridos
    if (!documentId) {
      throw new Error("Se requiere el ID del documento");
    }

    const response = await axios.delete(
      `${API_URL}/workflows/p2p/${clientId}/documents/${documentId}`
    );
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('documentDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('documentDeleting', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al eliminar el documento:", error.response?.data || error);
    throw error;
  }
};

/**
 * Envía correos electrónicos a proveedores seleccionados y al solicitante
 * @param {string} clientId - ID del cliente
 * @param {string} workflowId - ID del workflow
 * @param {string} orderId - ID de la orden
 * @param {Array} suppliers - Array de objetos proveedor con al menos id, name y contact_email
 * @param {string} requester_email - Email del solicitante
 * @param {string} subject - Asunto del correo (opcional)
 * @param {Array} attachments - Array de adjuntos (opcional)
 * @param {string} user_name - Nombre del usuario que envía el correo (opcional)
 * @returns {Promise<Object>} - Respuesta con el resultado del envío
 */
export const sendEmailToSupplierAndRequester = async (
  clientId, 
  workflowId, 
  orderId, 
  suppliers, 
  requester_email, 
  subject = "", 
  attachments = [],
  user_name = "Sistema"
) => {
  try {
    // Enviar evento para mostrar esqueleto
    const event = new CustomEvent('emailSending', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.post(
      `${API_URL}/workflows/p2p/${clientId}/workflows/${workflowId}/iprs/${orderId}/email-supplier-and-requester`,
      {
        suppliers,
        requester_email,
        subject,
        attachments,
        user_name
      }
    );
    
    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('emailSending', { detail: false });
    window.dispatchEvent(endEvent);
    
    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('emailSending', { detail: false });
    window.dispatchEvent(endEvent);
    
    console.error("Error al enviar email a proveedor y solicitante:", error.response?.data || error);
    throw error;
  }
};

/**
 * Envía correos electrónicos y whatsapp a proveedores seleccionados y al solicitante
 * @param {string} clientId - ID del cliente
 * @param {string} orderId - ID de la orden
 * @param {Array} suppliers - Array de objetos proveedor con al menos id, name y contact_email
 * @param {string} requester_email - Email del solicitante
 * @param {string} subject - Asunto del correo (opcional)
 * @param {Array} attachments - Array de adjuntos (opcional)
 * @param {string} user_name - Nombre del usuario que envía el correo (opcional)
 * @returns {Promise<Object>} - Respuesta con el resultado del envío
 */
export const sendPOConfirmationToSupplierAndRequester = async (
  clientId,
  orderId,
  suppliers,
  requester_email,
  subject = "",
  attachments = [],
  user_name = "Sistema",
  clientName = ""
) => {
  try {
    // Enviar evento para mostrar esqueleto
    const event = new CustomEvent('emailSending', { detail: true });
    window.dispatchEvent(event);

    const response = await axios.post(
      `${API_URL}/workflows/p2p/${clientId}/iprs/${orderId}/po-confirmation-supplier-and-requester`,
      {
        suppliers,
        requester_email,
        subject,
        attachments,
        user_name,
        clientName
      }
    );

    // Enviar evento para ocultar esqueleto
    const endEvent = new CustomEvent('emailSending', { detail: false });
    window.dispatchEvent(endEvent);

    return response.data;
  } catch (error) {
    // Enviar evento para ocultar esqueleto en caso de error
    const endEvent = new CustomEvent('emailSending', { detail: false });
    window.dispatchEvent(endEvent);

    console.error("Error al enviar confirmación de PO:", error.response?.data || error);
    throw error;
  }
};


